import React, {useCallback, useEffect, useState} from "react";
import Modal from "react-modal";
import css from "./CloseInterventionSidebar.scss";
import PropTypes from "prop-types";
import Icon from "../SF/MaterialIcon";
import Footer from "common/Footer";
import StyledButton from "common/StyledButton";
import InterventionDetails from "./InterventionDetails";
import InterventionCard from "./DrawerCard";
import useStarfishAPI from "../hooks/useStarfishAPI";
import {urlParams} from "utils/request";
import {useRefreshContext} from "./RefreshProvider";
import RadioButtonWithIcon from "./RadioButtonWithIcon";
import ErrorMessage from "common/ErrorMessage";
import classnames from "classnames";
import {RequestHelp, SFEntityID} from "utils/types";
import striptags from "striptags";
import {TextArea} from "semantic-ui-react";
import {isNonEmptyString} from "../utils/text";

// Milliseconds to wait before closing the close intervention sidebar.
const closeModalTimeoutMS = 300
// Maximum number of characters the student comment text area is allowed.
const commentMaxLength = 1250
// Toast message when a student closes an intervention.
const studentCloseableSuccessMessage = "Great job taking action! Your item has been removed from your dashboard.";
// Toast message when a student requests review of an intervention.
const closureReviewRequiredSuccessMessage = "Awesome! Your item has been sent for review.";

const getBlockIcon = () => {
    return (
        <div className={css['block-icon']} style={{backgroundColor: "#B94713"}}>
            <Icon aria-hidden={true} icon={'check_box'} />
        </div>
    );
}

const getBlockType = () => 'TO DO';

const getBlockDetails = (props) =>  <InterventionDetails {...props} />

const CloseInterventionSidebar = (props) => {
    const [interventionResolutionReasons, getInterventionResolutionReasonsAPI] = useStarfishAPI({ isNewAPI: true });
    const [selectedReason, setSelectedReason] = useState(null);
    const [comment, setComment] = useState("");
    const [availableReasons, setAvailableReason] = useState(null);
    const [closeInterventionResponse, closeInterventionAPI] = useStarfishAPI();
    const [errors, setErrors] = useState({commentError: null, reasonError: null, formError: null});
    const { refresh } = useRefreshContext();

    useEffect(() => {
        getInterventionResolutionReasonsAPI('../api/interventions/resolution-reasons', { method: 'GET' });
    }, []);

    const resolutionReasonsNotEmpty = () =>
        interventionResolutionReasons && interventionResolutionReasons.data && interventionResolutionReasons.data.length > 0;

    useEffect(() => {
        if (interventionResolutionReasons.success) {
            if (resolutionReasonsNotEmpty()) {
                setAvailableReason(interventionResolutionReasons.data.filter(x => x.interventionCategory === "TO_DO"));
            } else {
                setErrors(prevErrors => {
                    return {...prevErrors, formError: "There was an error retrieving the reason list for closing this item."};
                });
            }
        } else if (!interventionResolutionReasons.pending && interventionResolutionReasons.error) {
            setErrors(prevErrors => {
                return {...prevErrors, formError: interventionResolutionReasons.error};
            });
        }
    }, [interventionResolutionReasons]);

    useEffect(() => {
        if (!closeInterventionResponse.pending && closeInterventionResponse.error) {
            setErrors(prevErrors => {
                return {...prevErrors, formError: closeInterventionResponse.error};
            });
        } else if (closeInterventionResponse.success) {
            if (props.successToastMessage) {
                props.onClose();
                refresh();
                // For screenreaders, this slight delay is necessary for the toast
                // to be announced every time when widget content is refreshed
                setTimeout(() => {
                    props.studentCloseable && props.successToastMessage(studentCloseableSuccessMessage);
                    props.closureReviewRequired && props.successToastMessage(closureReviewRequiredSuccessMessage);
                }, 400);
            }
        }
    }, [closeInterventionResponse]);

    const submit = useCallback(() => {
        closeInterventionAPI(
            '../instructor/clearIntervention.html', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: urlParams({
                    interventionId: props.id,
                    todoResolutionReasonId: selectedReason
                }, false)
            });
    }, [selectedReason]);

    const requestReview = useCallback(() => {
        closeInterventionAPI(
            '../request-resolution.html', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    interventionId: props.id,
                    requestedResolutionReasonId: selectedReason,
                    requestedResolutionComment: striptags(comment)
                })
            });
    }, [selectedReason, comment]);

    let formHasError = false;

    const updateFieldErrors = (changedError) => {
        if (Object.values(changedError).some(Boolean)) {
            formHasError = true;
        }
        setErrors(prevErrors => {
            let combinedErrors = {...prevErrors, ...changedError, formError: null};
            if (Object.values(combinedErrors).some(Boolean)) {
                combinedErrors = {...combinedErrors, formError: "Complete any missing required fields."};
            }

            return combinedErrors;
        });
    }

    const validate = () => {
        if (!selectedReason) {
            updateFieldErrors( {reasonError: "Select a reason to mark item complete."});
        }
        if (props.closureReviewRequired && !isNonEmptyString(comment)) {
            updateFieldErrors({commentError: "Explain your reason for marking item complete."});
        }

        if (!formHasError) {
            props.studentCloseable && submit();
            props.closureReviewRequired && requestReview();
        }
    }

    const getResolutionReason = () => {
        if (!availableReasons) return
        const positiveReason = availableReasons.filter(x => x.resolutionCategory === "Positive")[0]
        const irrelevantReason = availableReasons.filter(x => x.resolutionCategory === "Irrelevant")[0]
        const otherReason = availableReasons.filter(x => x.resolutionCategory === "Unknown or Other")[0]

        if (!positiveReason || !irrelevantReason || !otherReason) return;

        return (
            <RadioButtonWithIcon
                name="resolution-reason"
                error={!!errors.reasonError}
                required={true}
                options={
                    [
                        {id: positiveReason.id, name: positiveReason.reason, icon: 'verified'},
                        {id: irrelevantReason.id, name: irrelevantReason.reason, icon: 'not_interested'},
                        props.closureReviewRequired && {id: otherReason.id, name: otherReason.reason, icon: 'comment'}
                    ].filter(Boolean)}
                value={selectedReason}
                onChange={(id) => {setSelectedReason(id); updateFieldErrors({reasonError: null})}} />
        );
    }

    const validateCommentLength = () => {
        if (isNonEmptyString(comment) && comment.length >= commentMaxLength) {
            return (
                <span className={css['comment-warning']} role='alert'>
                    <Icon icon='warning' aria-hidden='true' />You have reached the {commentMaxLength} character limit.
                </span>
            )
        }
    }

    const getCommentTextArea = () => {
        return (
            <>
                <div className={classnames(css['field-label'], css['share-details'], {[css['error']]: !!errors.commentError})}>
                    <span className={css['red-asterisk']} aria-hidden='true'>*</span> Please share details
                </div>
                <div className={classnames({[css['text-area-error']]: !!errors.commentError})}>
                    <TextArea
                        className={css['text-area']}
                        aria-label={'Please share details, Required'}
                        rows={3}
                        maxLength={commentMaxLength}
                        value={comment}
                        onChange={e => {setComment(e.target.value); updateFieldErrors({commentError: null});}} />
                </div>
                {validateCommentLength()}
                {errors.commentError && <div className={css['field-error']}><Icon aria-hidden="true" icon="error"/><span>{errors.commentError}</span></div>}
            </>
        )
    }

    Modal.setAppElement(document.getElementById("main-center"));
    return (
        <Modal
            isOpen={props.isOpen}
            onRequestClose={props.onClose}
            overlayClassName={{
                base: css["overlay"],
                afterOpen: css["overlay-after-open"],
                beforeClose: css["overlay-before-close"]
            }}
            className={{
                base: css["modal"],
                afterOpen: css["modal-after-open"],
                beforeClose: css["modal-before-close"]
            }}
            contentLabel="Mark Item Complete"
            closeTimeoutMS={closeModalTimeoutMS}>
            {errors.formError && <ErrorMessage message={errors.formError}/>}
            <div className={css['container']}>
                <div className={css['content']}>
                    <button
                        onClick={props.onClose}
                        aria-label="Close drawer"
                        className={css['close-button-todo']}>
                        <Icon aria-hidden={true} icon='close' />
                    </button>
                    <h2 className={css['header']}>Mark Item Complete</h2>
                    <div className={css['description']}>
                        {props.studentCloseable && <span>{props.raiser.name} and other connections will see that you took action on this item.
                            After you submit it, the item will be removed from your dashboard.</span>}

                        {props.closureReviewRequired && <span>After you select <b>Request Review</b>, {props.raiser.name} and other connections will
                            see that you want to mark this item complete and someone will review it. If approved, this item will be removed from your dashboard.</span>}
                    </div>
                    <div className={css['item-header']}>Item being marked complete:</div>
                    <InterventionCard
                        blockType={getBlockType()}
                        blockIcon={getBlockIcon()}
                        blockName={props.name}>
                        {getBlockDetails(props)}
                    </InterventionCard>
                    <div className={classnames(css['field-label'], {[css['error']]: !!errors.reasonError})}>
                        <span className={css['red-asterisk']} aria-hidden='true'>*</span> Mark item complete because:
                    </div>
                    {getResolutionReason()}
                    {errors.reasonError && <div className={css['field-error']}><Icon aria-hidden="true" icon="error"/><span>{errors.reasonError}</span></div>}
                    {props.closureReviewRequired && getCommentTextArea()}
                    {(props.studentCloseable && props.requestHelp.requestHelpEnabled) &&
                        <div className={css['request-help']}>
                            Neither of these options feel right? We&apos;re here to help.
                            <a href={'../student/request-help.html'} className={css['request-help-button']}>
                                <Icon icon='pan_tool' aria-hidden='true' className={css['request-help-icon']}/>
                                {props.requestHelp.studentHelpText}
                            </a>
                        </div>
                    }
                </div>
                <Footer
                    primaryButton={
                        <StyledButton onClick={validate}>
                            {props.studentCloseable && 'Submit'}
                            {props.closureReviewRequired && 'Request Review'}
                        </StyledButton>
                    }
                    secondaryButton={
                        <StyledButton asLink onClick={props.onClose}>
                            Cancel
                        </StyledButton>
                    }
                />
            </div>
        </Modal>
    );
}

CloseInterventionSidebar.propTypes = {
    id: SFEntityID.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    assignee: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
    }),
    dueDate: PropTypes.string,
    name: PropTypes.string.isRequired,
    raisedDate: PropTypes.string.isRequired,
    raiseNotes: PropTypes.string,
    raiser: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    section: PropTypes.shape({
        cmsCourseId: PropTypes.string,
        name: PropTypes.string,
    }),
    studentCloseable: PropTypes.bool.isRequired,
    closureReviewRequired: PropTypes.bool.isRequired,
    successToastMessage: PropTypes.func.isRequired,
    requestHelp: RequestHelp.isRequired
}

export default CloseInterventionSidebar;
